import { POST } from '../config/tuning';
import { hashUnit } from '../core/Rng';

/**
 * The maths behind the downlink post stack (SPEC §3.3.2), kept apart from the GL that applies it.
 *
 * Everything here is a pure function of scalars, so the two effects that carry gameplay signal —
 * the auto-exposure lag and the link-quality breakdown — can be unit-tested without a WebGL
 * context, and tuned by reading numbers rather than by squinting at a screenshot.
 *
 * Nothing in this file allocates: the two aggregate results are written into caller-owned
 * out-parameters, which the render loop holds at module scope.
 */

// Independent hash streams, so the hold decision and the tear position never correlate.
const SALT_HOLD = 0x51ce;
const SALT_TEAR = 0x2b17;
const SALT_TEAR_Y = 0x77a3;
const SALT_TEAR_SHIFT = 0x1d0f;
const SALT_JITTER_X = 0x3ea1;
const SALT_JITTER_Y = 0x6c45;

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

// --- Metering and exposure -------------------------------------------------------------------

/**
 * Encode a luminance as the 0..1 byte the metering chain stores. Unitless.
 *
 * Log encoding, because the meter averages *encoded* values and the average of log luminance is
 * the geometric mean — which is what an auto-exposure wants. Averaging linear luminance instead
 * would let one specular glint on the sea drag the whole gain down.
 */
export function encodeMeterLuminance(luminance: number): number {
  const stops = Math.log2(Math.max(luminance, 1e-6));
  const span = POST.meterEncodeMaxStops - POST.meterEncodeMinStops;
  return clamp01((stops - POST.meterEncodeMinStops) / span);
}

/** Inverse of `encodeMeterLuminance`: a metering byte in 0..255 back to a luminance. Unitless. */
export function decodeMeterByte(byte: number): number {
  const normalised = clamp01(byte / 255);
  const span = POST.meterEncodeMaxStops - POST.meterEncodeMinStops;
  return 2 ** (POST.meterEncodeMinStops + normalised * span);
}

/**
 * Gain the camera would settle at for a measured scene luminance, in stops (log2 of the linear
 * gain). Clamped, so a fully black or fully blown frame cannot drive the picture off the scale.
 */
export function targetExposureStops(measuredLuminance: number): number {
  const ratio = POST.exposureTargetLuminance / Math.max(measuredLuminance, 1e-6);
  const stops = Math.log2(ratio);
  if (stops < POST.exposureMinStops) return POST.exposureMinStops;
  if (stops > POST.exposureMaxStops) return POST.exposureMaxStops;
  return stops;
}

/**
 * Advance the exposure toward its target with the 0.6 s adaptation lag of SPEC §3.3.2. Stops in,
 * stops out; `dtS` is the render frame time in seconds, since this is presentation, not physics.
 *
 * Exponential rather than linear approach, and framed as `1 - exp(-dt/τ)` rather than `dt·k`, so
 * the lag is the same whether the frame took 8 ms or 40 — a linear step would make the picture
 * settle faster on a fast machine, which is exactly the kind of frame-rate dependence SPEC §4.3
 * spends its time removing from the simulation.
 */
export function adaptExposureStops(currentStops: number, targetStops: number, dtS: number): number {
  if (dtS <= 0) return currentStops;
  const blend = 1 - Math.exp(-dtS / POST.exposureAdaptS);
  return currentStops + (targetStops - currentStops) * blend;
}

/**
 * Grain multiplier for a given exposure gain, unitless. A sensor pushed to +2 stops in the dark
 * is noisier than the same sensor stopped down in daylight (SPEC §3.3.2 item 9).
 */
export function grainGainFromStops(exposureStops: number): number {
  return 1 + Math.max(0, exposureStops) * 0.6;
}

// --- Rolling shutter -------------------------------------------------------------------------

/**
 * Horizontal shear from yaw rate (SPEC §3.3.2 item 8), as a fraction of frame width between the
 * top and bottom rows.
 *
 * The sensor reads out top to bottom over `rollingShutterS`, so the bottom row sees the world
 * `yawRate · readout` radians later than the top; dividing by the horizontal field of view turns
 * those radians into a fraction of the frame. Yaw rate in rad/s, FOV in radians.
 */
export function rollingShutterSkew(yawRateRadS: number, horizontalFovRad: number): number {
  return (yawRateRadS * POST.rollingShutterS) / Math.max(horizontalFovRad, 1e-3);
}

// --- Link quality ----------------------------------------------------------------------------

/** How the picture is breaking up, all unitless and all driven by one link-quality value. */
export interface DownlinkArtefacts {
  /** Fraction of 8×8 macroblocks collapsed to their average colour, 0..1. */
  blockCoverage: number;
  /** Quantisation levels per channel. 255 is clean. */
  quantLevels: number;
  /** Probability that a downlink frame is held rather than refreshed, 0..1. */
  holdProbability: number;
  /** Probability that a frame tears, 0..1. */
  tearProbability: number;
  /** Whole-frame positional jitter, fraction of frame width. */
  jitterOffset: number;
}

export function createDownlinkArtefacts(): DownlinkArtefacts {
  return {
    blockCoverage: 0,
    quantLevels: POST.quantLevelsClean,
    holdProbability: 0,
    tearProbability: 0,
    jitterOffset: 0,
  };
}

/** Degradation in 0..1: nothing at or above the onset, rising to full at zero link. */
function degradation(linkQuality: number, onset: number): number {
  if (linkQuality >= onset) return 0;
  const t = clamp01((onset - linkQuality) / onset);
  // Squared, so the picture stays usable through the first half of the fall and then goes
  // quickly. A linear ramp reads as a slow fade rather than as a link giving out.
  return t * t;
}

/**
 * Map link quality in [0, 1] onto every artefact it drives (SPEC §3.3.2 items 6 and 7). This is
 * the single place the gameplay signal becomes a picture, which is why it is one function.
 */
export function downlinkArtefacts(linkQuality: number, out: DownlinkArtefacts): DownlinkArtefacts {
  const quality = clamp01(linkQuality);

  const compression = degradation(quality, POST.blockOnsetLink);
  out.blockCoverage = POST.blockMaxCoverage * compression;
  // Geometric interpolation of the quantiser: levels are perceived logarithmically, so a linear
  // walk from 255 to 6 would look like nothing at all until the very bottom of the range.
  out.quantLevels =
    POST.quantLevelsClean * (POST.quantLevelsWorst / POST.quantLevelsClean) ** compression;

  const stutter = degradation(quality, POST.jitterOnsetLink);
  out.holdProbability = POST.holdMaxProbability * stutter;
  out.jitterOffset = POST.jitterMaxOffset * stutter;
  out.tearProbability = POST.tearMaxProbability * degradation(quality, POST.tearOnsetLink);

  return out;
}

/** What this particular downlink frame is doing. All offsets are fractions of frame width. */
export interface DownlinkFrame {
  /** True when the previous frame is being repeated instead of a fresh one. */
  hold: boolean;
  /** Normalised height of the tear, 0 at the bottom of the frame; 1 means no tear. */
  tearY: number;
  tearShift: number;
  offsetX: number;
  offsetY: number;
}

export function createDownlinkFrame(): DownlinkFrame {
  return { hold: false, tearY: 1, tearShift: 0, offsetX: 0, offsetY: 0 };
}

/**
 * Resolve the hold/tear/jitter state for the downlink frame current at `simTimeS` seconds.
 *
 * Keyed on the *downlink* frame index rather than the render frame, so a stutter lasts a fixed
 * length of time rather than a fixed number of frames — the same breakup at 30 fps and at 144 —
 * and hashed rather than drawn from a stream, so the same moment of the same flight always breaks
 * up the same way. `Math.random` would have been the obvious choice and is banned for exactly the
 * reasons that matter here (SPEC §4.3).
 */
export function downlinkFrame(
  simTimeS: number,
  artefacts: DownlinkArtefacts,
  out: DownlinkFrame,
): DownlinkFrame {
  const index = Math.floor(simTimeS * POST.linkFrameHz);

  out.hold = artefacts.holdProbability > 0 && hashUnit(index, SALT_HOLD) < artefacts.holdProbability;

  if (artefacts.tearProbability > 0 && hashUnit(index, SALT_TEAR) < artefacts.tearProbability) {
    out.tearY = hashUnit(index, SALT_TEAR_Y);
    out.tearShift = (hashUnit(index, SALT_TEAR_SHIFT) * 2 - 1) * POST.tearMaxShift;
  } else {
    out.tearY = 1;
    out.tearShift = 0;
  }

  const offset = artefacts.jitterOffset;
  out.offsetX = offset === 0 ? 0 : (hashUnit(index, SALT_JITTER_X) * 2 - 1) * offset;
  out.offsetY = offset === 0 ? 0 : (hashUnit(index, SALT_JITTER_Y) * 2 - 1) * offset;

  return out;
}
